import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, Chip, Grid, IconButton, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, Controller } from 'react-hook-form';
import Swal from 'sweetalert2';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch } from "react-redux";
import Loading from '../../components/Loader/Loading';
import NoRowsLayout from '../../components/Layout/NoRowsLayout';
import { LocationService } from './LocationService';

const locationInitialData = { name: '', lat: '', lng: '', radius: 15, isActive: true };

export default function LocationPage() {
  const [locationList, setLocationList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [pageTitle, setPageTitle] = useState('Add Location');

   const menuDispatch = useDispatch();
    useEffect(() => {
        menuDispatch({
            type: 'menuSelect',
            payload: 'locations'
        });
    }, []);
  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    mode: 'onChange',
    defaultValues: locationInitialData,
  });

  const fetchLocationList = async () => {
    setLoadingList(true);
    try {
      const res = await LocationService.list();
      if (res.success) setLocationList(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoadingList(false);
  };

  useEffect(() => {
    fetchLocationList();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      setPageTitle('Edit Location');
      reset({ name: selectedLocation.name, lat: selectedLocation.lat, lng: selectedLocation.lng, radius: selectedLocation.radius ?? 15, isActive: selectedLocation.isActive });
    } else {
      setPageTitle('Add Location');
      reset(locationInitialData);
    }
  }, [selectedLocation]);

  const resetForm = () => {
    setSelectedLocation(null);
    reset(locationInitialData);
  };

  const onSubmit = async (data) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to ${selectedLocation ? 'update' : 'save'} this location?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${selectedLocation ? 'update' : 'save'}`,
      cancelButtonText: 'No, cancel',
      allowOutsideClick: false,
    });
    if (!result.isConfirmed) return;

    Swal.fire({ title: 'Please wait...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    const payload = { name: data.name, lat: parseFloat(data.lat), lng: parseFloat(data.lng), radius: parseFloat(data.radius) || 15, isActive: data.isActive };
    try {
      const res = selectedLocation
        ? await LocationService.update(selectedLocation._id, payload)
        : await LocationService.create(payload);
      if (res.success) {
        Swal.fire({ icon: 'success', title: 'Success', text: selectedLocation ? 'Location updated' : 'Location saved', timer: 1000, showConfirmButton: false });
        fetchLocationList();
        resetForm();
      } else {
        Swal.fire({ icon: 'error', title: 'Oops...', text: res.message || 'Something went wrong' });
      }
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!' });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?', text: "You won't be able to revert this!", icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Yes, delete it!', cancelButtonText: 'No, keep it',
      allowOutsideClick: false,
    });
    if (!result.isConfirmed) return;
    Swal.fire({ title: 'Processing', html: 'Please wait...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const res = await LocationService.remove(id);
      if (res.success) {
        Swal.fire({ title: 'Deleted!', text: 'Location has been deleted.', icon: 'success', timer: 1500, showConfirmButton: false });
        fetchLocationList();
      } else {
        Swal.fire('Failed!', '', 'error');
      }
    } catch (e) {
      Swal.fire('Failed!', '', 'error');
    }
  };

  const handleToggleStatus = async (row) => {
    try {
      const res = await LocationService.toggleStatus(row._id);
      if (res.success) fetchLocationList();
    } catch (e) {
      console.error(e);
    }
  };

  const columns = [
    {
      field: 'sno', headerName: 'S.No.', headerAlign: 'center', flex: 0.5,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Typography sx={{ fontSize: '.7rem', fontWeight: 600, color: '#3e3e3e' }}>
            {locationList.findIndex(l => l._id === params.row._id) + 1}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'name', headerName: 'City Name', headerAlign: 'center', flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pl: 1 }}>
          <Typography sx={{ fontSize: '.7rem', fontWeight: 600, color: '#3e3e3e' }}>{params.row.name}</Typography>
        </Box>
      ),
    },
    {
      field: 'lat', headerName: 'Latitude', headerAlign: 'center', flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Typography sx={{ fontSize: '.7rem', fontFamily: 'monospace', color: '#555' }}>{params.row.lat}</Typography>
        </Box>
      ),
    },
    {
      field: 'lng', headerName: 'Longitude', headerAlign: 'center', flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Typography sx={{ fontSize: '.7rem', fontFamily: 'monospace', color: '#555' }}>{params.row.lng}</Typography>
        </Box>
      ),
    },
    {
      field: 'radius', headerName: 'Radius (km)', headerAlign: 'center', flex: 1.2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Typography sx={{ fontSize: '.7rem', fontWeight: 600, color: '#1976d2' }}>{params.row.radius ?? 15} km</Typography>
        </Box>
      ),
    },
    {
      field: 'isActive', headerName: 'Status', headerAlign: 'center', flex: 1.2, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', gap: 0.5 }}>
          <Switch
            checked={params.row.isActive}
            onChange={() => handleToggleStatus(params.row)}
            size="small"
          />
          <Chip
            label={params.row.isActive ? 'Active' : 'Inactive'}
            color={params.row.isActive ? 'success' : 'default'}
            size="small"
            sx={{ fontSize: '0.65rem', height: 'fit-content' }}
          />
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', headerAlign: 'center', flex: 1, sortable: false, filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="warning"
              disabled={!!selectedLocation}
              onClick={() => setSelectedLocation(params.row)}
            >
              <EditIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(params.row._id)}>
              <DeleteIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Grid container spacing={1}>
      {/* ── Add / Edit Form ── */}
      <Grid item xs={12} lg={4}>
        <Card>
          <Box sx={{ p: '.5% 1%', border: '2px solid lightGray', bgcolor: 'lightGray' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{pageTitle}</Typography>
          </Box>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 1.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  City Name <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                </Typography>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'City name is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      placeholder="e.g. Noida"
                      error={!!error}
                      helperText={error?.message}
                      inputProps={{ style: { fontSize: '0.65rem' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Latitude <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                </Typography>
                <Controller
                  name="lat"
                  control={control}
                  rules={{
                    required: 'Latitude is required',
                    validate: v => (!isNaN(parseFloat(v)) && isFinite(v)) || 'Must be a valid number',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      placeholder="e.g. 28.6289"
                      error={!!error}
                      helperText={error?.message}
                      inputProps={{ style: { fontSize: '0.65rem' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Longitude <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                </Typography>
                <Controller
                  name="lng"
                  control={control}
                  rules={{
                    required: 'Longitude is required',
                    validate: v => (!isNaN(parseFloat(v)) && isFinite(v)) || 'Must be a valid number',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      placeholder="e.g. 77.3648"
                      error={!!error}
                      helperText={error?.message}
                      inputProps={{ style: { fontSize: '0.65rem' } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Radius (km) <Box component="span" sx={{ color: '#d32f2f' }}>*</Box>
                </Typography>
                <Controller
                  name="radius"
                  control={control}
                  rules={{
                    required: 'Radius is required',
                    validate: v => (!isNaN(parseFloat(v)) && parseFloat(v) > 0) || 'Must be a positive number',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      size="small"
                      fullWidth
                      type="number"
                      placeholder="e.g. 15"
                      error={!!error}
                      helperText={error?.message}
                      inputProps={{ style: { fontSize: '0.65rem' }, min: 1, step: 1 }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={e => field.onChange(e.target.checked)}
                        size="small"
                      />
                    )}
                  />
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Active</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                {selectedLocation && (
                  <Button variant="outlined" size="small" onClick={resetForm}
                    sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" variant="contained" size="small" disabled={isSubmitting}
                  sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
                  {selectedLocation ? 'Update' : 'Save'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Card>
      </Grid>

      {/* ── Location List ── */}
      <Grid item xs={12} lg={8}>
        <Card sx={{ minWidth: '100%' }}>
          <Box sx={{ p: '.5% 1%', border: '2px solid lightGray', bgcolor: 'lightGray' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Location List</Typography>
          </Box>
          <Box sx={{ overflow: 'auto' }}>
            {loadingList && <Loading />}
            <DataGrid
              rows={locationList}
              columns={columns}
              getRowId={(row) => row._id}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              loading={loadingList}
              disableColumnMenu
              disableRowSelectionOnClick
              autoHeight
              getRowHeight={() => 'auto'}
              slots={{ loadingOverlay: Loading, noRowsOverlay: NoRowsLayout }}
              sx={{
                '& .MuiDataGrid-cell': {
                  border: 1, borderRight: 0, borderTop: 0, borderColor: 'lightgrey',
                  display: 'flex', alignItems: 'center',
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: '2px solid rgba(224,224,224,1)',
                  borderTop: '2px solid rgba(224,224,224,1)',
                  fontWeight: 600, fontSize: '0.75rem',
                  minHeight: '0 !important', height: '2rem',
                },
                '& .MuiDataGrid-columnHeader': {
                  borderLeft: '1px solid rgba(224,224,224,1)',
                  '&:last-child': { borderRight: 'none' },
                },
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, color: '#3e3e3e' },
                '& .MuiDataGrid-row': { fontSize: '0.75rem' },
              }}
            />
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
}
